import { useCallback, useState } from 'react'
import { Alert, Button, Card, Group, List, Stack, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconBuildingHospital,
  IconFlask,
  IconInfoCircle,
  IconStethoscope,
  IconTestPipe,
  IconTrash,
  IconVocabulary,
} from '@tabler/icons-react'
import { Navigation } from './components'
import { SeedSharedPanelsCard } from './components/SeedSharedPanelsCard'
import { FeePolicyCard } from './components/FeePolicyCard'
import { useMiddlewareClient } from '@/hooks'

function isNetworkOrTimeoutError(error: unknown): boolean {
  if (error instanceof TypeError) return true // fetch network errors
  const msg = (error as Error)?.message?.toLowerCase() ?? ''
  return (
    msg.includes('timeout') ||
    msg.includes('network') ||
    msg.includes('aborted') ||
    msg.includes('failed to fetch')
  )
}

function useMigration<T>(opts: {
  endpoint: string
  title: string
  formatSuccess: (result: T) => string
}) {
  const middleware = useMiddlewareClient()
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<T | null>(null)

  const run = useCallback(async () => {
    const notifId = `migration-${opts.endpoint}`
    setLoading(true)
    notifications.show({
      id: notifId,
      loading: true,
      title: opts.title,
      message: 'Running — this may take a few minutes...',
      autoClose: false,
      withCloseButton: false,
    })

    try {
      const response = await middleware.post(opts.endpoint)
      setResult(response)
      notifications.update({
        id: notifId,
        loading: false,
        color: 'green',
        title: opts.title,
        message: opts.formatSuccess(response),
        autoClose: 5000,
        withCloseButton: true,
      })
    } catch (error) {
      if (isNetworkOrTimeoutError(error)) {
        notifications.update({
          id: notifId,
          loading: false,
          color: 'yellow',
          title: `${opts.title} — connection lost`,
          message:
            'The migration may still be running on the server. Safe to re-run — existing records are skipped.',
          autoClose: false,
          withCloseButton: true,
        })
      } else {
        notifications.update({
          id: notifId,
          loading: false,
          color: 'red',
          title: `${opts.title} — failed`,
          message: (error as Error)?.message || 'An unexpected error occurred',
          autoClose: false,
          withCloseButton: true,
        })
      }
    } finally {
      setLoading(false)
    }
  }, [middleware, opts])

  return { loading, result, run }
}

export function DataMigrations() {
  const parentLabs = useMigration<{
    created: number
    skipped: number
    repairedTelecom: number
    total: number
  }>({
    endpoint: '/super/seed-parent-labs',
    title: 'Seed Parent Labs',
    // Call out repairs: a non-zero count means a lab existed without its fax
    // (the catalog seeder having created it first) and this run fixed it.
    formatSuccess: (r) =>
      `${r.created} created, ${r.skipped} skipped (${r.total} total)` +
      (r.repairedTelecom ? `, ${r.repairedTelecom} telecom repaired` : ''),
  })

  const radFacilities = useMigration<{
    created: number
    skipped: number
    updated: number
    total: number
  }>({
    endpoint: '/super/seed-radiology-facilities',
    title: 'Seed Radiology Facilities',
    formatSuccess: (r) =>
      `${r.created} created, ${r.updated} logos updated, ${r.skipped} skipped (${r.total} total)`,
  })

  const labLocations = useMigration<{ created: number; skipped: number; total: number }>({
    endpoint: '/super/seed-lab-locations',
    title: 'Seed Lab Locations',
    formatSuccess: (r) => `${r.created} created, ${r.skipped} skipped (${r.total} total)`,
  })

  const testCatalog = useMigration<{
    created: number
    updated: number
    retired: number
    skipped: number
    total: number
  }>({
    endpoint: '/super/seed-test-catalog',
    title: 'Seed Test Catalog',
    formatSuccess: (r) =>
      `${r.created} created, ${r.updated} corrected, ${r.retired} retired, ` +
      `${r.skipped} unchanged (${r.total} total)`,
  })

  const pruneOrgs = useMigration<{ deleted: number; kept: number; manualReview: string[] }>({
    endpoint: '/super/prune-unused-lab-organizations',
    title: 'Prune Unused Lab Organizations',
    formatSuccess: (r) =>
      `${r.deleted} deleted, ${r.kept} kept, ${r.manualReview.length} need manual review`,
  })

  const cptValueSets = useMigration<{ created: number; updated: number; total: number }>({
    endpoint: '/super/seed-specialty-cpt-valuesets',
    title: 'Seed Specialty → CPT ValueSets',
    formatSuccess: (r) => `${r.created} created, ${r.updated} updated (${r.total} total)`,
  })

  const providerDirectory = useMigration<{
    status: string
    bucket: string
    key: string
    startedAt: string
    objectSize?: number
  }>({
    endpoint: '/super/seed-provider-directory',
    title: 'Seed Provider Directory',
    formatSuccess: (r) =>
      `Started at ${new Date(r.startedAt).toLocaleTimeString()} — reading s3://${r.bucket}/${r.key}. ` +
      `Job runs in background; monitor server logs or FHIR resource counts for progress.`,
  })

  const undoProviderDirectory = useMigration<{ status: string; startedAt: string }>({
    endpoint: '/super/undo-provider-directory',
    title: 'Undo Provider Directory Seed',
    formatSuccess: (r) =>
      `Started at ${new Date(r.startedAt).toLocaleTimeString()} — deleting tagged resources across all projects. ` +
      `Runs in background; monitor server logs for per-project counts and completion.`,
  })

  return (
    <>
      <Navigation />
      <div className="px-6 py-4">
        <Title order={2} mb="md">
          Data Migrations
        </Title>

        <Stack gap="md" maw={820}>
          <Alert variant="light" icon={<IconInfoCircle size={18} />}>
            Idempotent data migrations. Safe to run multiple times — existing records are skipped.
          </Alert>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Parent Labs</Text>
                  <Text c="dimmed" size="sm">
                    Creates the 6 parent laboratory Organizations (Invitae, Labcorp, AccuReference,
                    Exact Sciences, Quest Diagnostics, GTI). Required before seeding lab locations
                    and before seeding the test catalog. Skips any that already exist, but fills in
                    a missing fax or phone on one that does.
                  </Text>
                </div>
                <Button
                  leftSection={<IconFlask size={18} />}
                  loading={parentLabs.loading}
                  onClick={parentLabs.run}
                >
                  Run Seed
                </Button>
              </Group>

              {parentLabs.result && (
                <Text size="sm" c="dimmed">
                  Last run: {parentLabs.result.created} created, {parentLabs.result.skipped} skipped
                  ({parentLabs.result.total} total)
                  {parentLabs.result.repairedTelecom
                    ? `, ${parentLabs.result.repairedTelecom} telecom repaired`
                    : ''}
                  .
                </Text>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Radiology Facilities</Text>
                  <Text c="dimmed" size="sm">
                    Creates 3,000+ radiology facilities as Organization resources from the master
                    facility list. Skips any that already exist (matched by exact name). Adds logos
                    to existing facilities.
                  </Text>
                </div>
                <Button
                  leftSection={<IconBuildingHospital size={18} />}
                  loading={radFacilities.loading}
                  onClick={radFacilities.run}
                >
                  Run Seed
                </Button>
              </Group>

              {radFacilities.result && (
                <Text size="sm" c="dimmed">
                  Last run: {radFacilities.result.created} created, {radFacilities.result.updated}{' '}
                  logos updated, {radFacilities.result.skipped} skipped (
                  {radFacilities.result.total} total).
                </Text>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Lab Locations</Text>
                  <Text c="dimmed" size="sm">
                    Creates 1,200+ Labcorp and 400+ Quest Diagnostics patient service center
                    locations as Organization resources from the NPPES NPI Registry dataset. Each
                    location includes address, geolocation, and phone. Linked to parent lab via
                    partOf. Skips existing locations.
                  </Text>
                </div>
                <Button
                  leftSection={<IconTestPipe size={18} />}
                  loading={labLocations.loading}
                  onClick={labLocations.run}
                >
                  Run Seed
                </Button>
              </Group>

              {labLocations.result && (
                <Text size="sm" c="dimmed">
                  Last run: {labLocations.result.created} created, {labLocations.result.skipped}{' '}
                  skipped ({labLocations.result.total} total).
                </Text>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Prune Unused Lab Organizations</Text>
                  <Text c="dimmed" size="sm">
                    Deletes unused duplicate parent lab Organizations inside the BeHuman project.
                    Canonical labs and any lab still referenced by a location or test catalog entry
                    are kept.
                  </Text>
                </div>
                <Button
                  color="red"
                  leftSection={<IconTrash size={18} />}
                  loading={pruneOrgs.loading}
                  onClick={pruneOrgs.run}
                >
                  Run Prune
                </Button>
              </Group>

              {pruneOrgs.result && (
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Last run: {pruneOrgs.result.deleted} deleted, {pruneOrgs.result.kept} kept,{' '}
                    {pruneOrgs.result.manualReview.length} marked for manual review.
                  </Text>
                  {pruneOrgs.result.manualReview.length > 0 && (
                    <List size="sm" spacing={4}>
                      {pruneOrgs.result.manualReview.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  )}
                </Stack>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Test Catalog</Text>
                  <Text c="dimmed" size="sm">
                    Creates ActivityDefinition resources for all lab tests across Labcorp (165),
                    Quest Diagnostics (4,500+), AccuReference (270+), and GTI (68). Each test
                    includes category, specimen type, and favorite flag. Safe to re-run: tests
                    already seeded under an outdated name or category are corrected, and codes no
                    longer in the catalog are retired.
                  </Text>
                </div>
                <Button
                  leftSection={<IconFlask size={18} />}
                  loading={testCatalog.loading}
                  onClick={testCatalog.run}
                >
                  Run Seed
                </Button>
              </Group>

              {testCatalog.result && (
                <Text size="sm" c="dimmed">
                  Last run: {testCatalog.result.created} created, {testCatalog.result.updated}{' '}
                  corrected, {testCatalog.result.retired} retired, {testCatalog.result.skipped}{' '}
                  unchanged ({testCatalog.result.total} total).
                </Text>
              )}
            </Stack>
          </Card>

          <SeedSharedPanelsCard />

          <FeePolicyCard />

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Specialty → CPT ValueSets</Text>
                  <Text c="dimmed" size="sm">
                    Creates one FHIR ValueSet per NUCC specialty containing the CPT codes the
                    procedure referral form should surface (plus shared consultation codes). Safe to
                    re-run — existing ValueSets are updated in place.
                  </Text>
                </div>
                <Button
                  leftSection={<IconVocabulary size={18} />}
                  loading={cptValueSets.loading}
                  onClick={cptValueSets.run}
                >
                  Run Seed
                </Button>
              </Group>

              {cptValueSets.result && (
                <Text size="sm" c="dimmed">
                  Last run: {cptValueSets.result.created} created, {cptValueSets.result.updated}{' '}
                  updated ({cptValueSets.result.total} total).
                </Text>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Seed Provider Directory</Text>
                  <Text c="dimmed" size="sm">
                    Streams the NDJSON artifact at <code>PROVIDER_DIRECTORY_S3_BUCKET/_KEY</code>{' '}
                    into Medplum as Practitioner, Organization, Location, and PractitionerRole
                    resources tagged <code>provider-directory</code>. Runs in the background — the
                    request returns immediately and progress is logged server-side. Idempotent via
                    NPI.
                  </Text>
                </div>
                <Button
                  leftSection={<IconStethoscope size={18} />}
                  loading={providerDirectory.loading}
                  onClick={providerDirectory.run}
                >
                  Run Seed
                </Button>
              </Group>

              {providerDirectory.result && (
                <Text size="sm" c="dimmed">
                  Started at {new Date(providerDirectory.result.startedAt).toLocaleString()} —
                  reading{' '}
                  <code>
                    s3://{providerDirectory.result.bucket}/{providerDirectory.result.key}
                  </code>
                  {providerDirectory.result.objectSize != null &&
                    ` (${(providerDirectory.result.objectSize / 1024 / 1024).toFixed(1)} MB)`}
                  . Check server logs for progress/completion.
                </Text>
              )}
            </Stack>
          </Card>

          <Card withBorder shadow="sm" radius="md" p="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600}>Undo Provider Directory Seed</Text>
                  <Text c="dimmed" size="sm">
                    Deletes every Practitioner, Organization, Location, and PractitionerRole tagged{' '}
                    <code>provider-directory</code> across <strong>all projects</strong>. Intended
                    as a cleanup for bad seeds before re-running. Runs in the background — monitor
                    server logs for per-project deletion counts.
                  </Text>
                </div>
                <Button
                  color="red"
                  leftSection={<IconTrash size={18} />}
                  loading={undoProviderDirectory.loading}
                  onClick={undoProviderDirectory.run}
                >
                  Run Undo
                </Button>
              </Group>

              {undoProviderDirectory.result && (
                <Text size="sm" c="dimmed">
                  Started at {new Date(undoProviderDirectory.result.startedAt).toLocaleString()}.
                  Check server logs for per-project deletion counts and completion.
                </Text>
              )}
            </Stack>
          </Card>
        </Stack>
      </div>
    </>
  )
}
